/**
 * menu-config-context.tsx — provider + hook da config de menu (Parte 4)
 *
 * Vive no shell (envolvendo o `AppShell`), para que a SIDEBAR e a tela de
 * Configurações leiam a MESMA instância/estado: um único `onSnapshot` em
 * `tenants/{tenantId}/config/menu` hidrata os dois (§3.4 e §7 do desenho). O
 * hook de leitura da sidebar consome `config`; a tela de Configurações consome
 * `config` + a ação `salvar`.
 *
 * Espelha a mecânica de `use-deliberacoes-repo.ts`:
 *   - `useMemo` do repo por `tenantId`/`atualizadoPorUid` (reconstrói só quando a
 *     identidade muda), com lineage do `useTenant`/`useOrbUser` (nunca de input
 *     livre; anti-planting);
 *   - hidratação via `observar()` (tempo real) num único `useEffect`;
 *   - `salvar` OTIMISTA + erro honesto (mesma mecânica das deliberações).
 *
 * FAIL-CLOSED honesto: sem `tenantId` ou sem `uid` (não autenticado / sem tenant
 * / modo demo sem firebase), o provider NÃO monta o repo Firestore e devolve
 * `MENU_CONFIG_DEFAULT` (tudo ligado), `carregando=false`, sem erro. A sidebar
 * segue mostrando tudo que o RBAC permite; nunca crasha, nunca escreve sem
 * autoria. A config só RESTRINGE, nunca AMPLIA além do RBAC.
 *
 * Sem PII. `atualizadoPor` é nome de exibição (nunca email). Sem travessão na
 * copy de erro.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { db } from "@/lib/firebase";
import { useOrbUser } from "@/features/auth/AuthContext";
import { useTenant } from "@/features/shell/TenantContext";
import type { MenuConfig } from "./menu-config";
import { MENU_CONFIG_DEFAULT } from "./menu-config";
import type { MenuConfigRepo } from "./menu-config-repo";
import { criarRepoFirestore } from "./menu-config-repo-firestore";

// ---------------------------------------------------------------------------
// Forma exposta pelo contexto
// ---------------------------------------------------------------------------

/**
 * O que o provider entrega. `config` é sempre um `MenuConfig` válido (fail-safe:
 * default quando não há tenant/dado). `salvar` é otimista; a falha de escrita
 * aparece em `erro`. `carregando` só é true enquanto a primeira hidratação do
 * modo real não retornou.
 */
export interface MenuConfigContextValue {
  /** Config vigente do tenant. Default (tudo ligado) quando ausente/sem auth. */
  readonly config: MenuConfig;
  /**
   * Substitui o estado inteiro dos toggles (doc único, idempotente). Otimista;
   * erro de escrita vai para `erro`. Property (não method) para a página
   * desestruturar sem o falso positivo `unbound-method`.
   */
  readonly salvar: (c: MenuConfig) => void;
  /** Mensagem honesta de falha (leitura ou escrita); ausente quando tudo ok. */
  readonly erro?: string;
  /** True enquanto a primeira hidratação do modo real não retornou. */
  readonly carregando: boolean;
}

/** Texto honesto quando a persistência de escrita não confirma. */
const ERRO_ESCRITA =
  "Não foi possível salvar. Sua alteração vale nesta sessão; tente novamente para persistir.";
/** Texto honesto quando a leitura da config do tenant falha. */
const ERRO_LEITURA = "Não foi possível carregar a configuração do menu.";

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

const MenuConfigContext = createContext<MenuConfigContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Mantém UM `onSnapshot` da config do tenant e o compartilha com toda a árvore
 * (sidebar + tela de Configurações). Reconstrói o repo quando a identidade
 * (tenant/uid/displayName) muda. Fail-closed: sem identidade completa, não monta
 * o repo Firestore e serve o default (tudo ligado), sem erro.
 */
export function MenuConfigProvider({ children }: { children: ReactNode }) {
  const orbUser = useOrbUser();
  const { tenantId } = useTenant();

  const atualizadoPorUid = orbUser?.uid ?? null;
  // Nome de exibição (zero PII). Sem nome, cai para um rótulo genérico de gestor.
  const atualizadoPor = orbUser?.displayName ?? "Gestor";

  // Constrói o repo real só com identidade completa; senão null (fail-closed):
  // a config fica no default e não há escrita sem autoria.
  const repo = useMemo<MenuConfigRepo | null>(() => {
    if (tenantId === null || atualizadoPorUid === null) return null;
    return criarRepoFirestore({
      db,
      tenantId,
      atualizadoPorUid,
      atualizadoPor,
    });
  }, [tenantId, atualizadoPorUid, atualizadoPor]);

  const [config, setConfig] = useState<MenuConfig>(MENU_CONFIG_DEFAULT);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [carregando, setCarregando] = useState<boolean>(false);

  // Guarda o repo vigente para `salvar` sempre escrever no ativo (sem recriar o
  // callback a cada hidratação e sem fechar sobre um repo obsoleto).
  const repoRef = useRef<MenuConfigRepo | null>(repo);
  repoRef.current = repo;

  // Hidratação: reseta ao trocar de repo e liga a fonte. `observar()` (tempo
  // real) tem precedência; sem ele, `ler()` uma vez. Fail-closed: sem repo
  // (sem auth), volta ao default sem erro (é ausência de dado, não falha).
  useEffect(() => {
    setErro(undefined);

    if (repo === null) {
      setConfig(MENU_CONFIG_DEFAULT);
      setCarregando(false);
      return;
    }

    let ativo = true;

    if (repo.observar !== undefined) {
      setCarregando(true);
      const parar = repo.observar((c) => {
        if (!ativo) return;
        setConfig(c);
        setCarregando(false);
      });
      return () => {
        ativo = false;
        parar();
      };
    }

    setCarregando(true);
    Promise.resolve(repo.ler())
      .then((c) => {
        if (!ativo) return;
        setConfig(c);
        setCarregando(false);
      })
      .catch(() => {
        if (!ativo) return;
        // Fail-safe: leitura falhou => default (tudo ligado), com erro honesto.
        setConfig(MENU_CONFIG_DEFAULT);
        setErro(ERRO_LEITURA);
        setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [repo]);

  const salvar = useCallback((c: MenuConfig) => {
    const alvo = repoRef.current;
    // Otimista: o estado local muda na hora (a sidebar reprojeta). Se o repo
    // observa, ele reemitirá o estado completo (idempotente por doc único);
    // mesmo assim a mudança local garante resposta imediata.
    setConfig(c);
    setErro(undefined);

    if (alvo === null) {
      // Sem auth: nada a persistir. A alteração vale só nesta sessão.
      setErro(ERRO_ESCRITA);
      return;
    }

    // A escrita pode ser sync (memória) ou async (Firestore); trata ambos.
    Promise.resolve(alvo.salvar(c)).catch(() => {
      setErro(ERRO_ESCRITA);
    });
  }, []);

  const valor = useMemo<MenuConfigContextValue>(
    () => ({ config, salvar, erro, carregando }),
    [config, salvar, erro, carregando],
  );

  return (
    <MenuConfigContext.Provider value={valor}>
      {children}
    </MenuConfigContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Consome a config de menu do provider mais próximo. Fail-closed defensivo: se
 * usado FORA do provider (montagem parcial em teste, modo demo sem shell),
 * devolve o default (tudo ligado), sem erro, em vez de lançar. Assim a sidebar
 * nunca morre por falta do provider; o RBAC continua sendo a barreira real.
 */
// eslint-disable-next-line react-refresh/only-export-components -- padrão context: provider + hook no mesmo arquivo
export function useMenuConfig(): MenuConfigContextValue {
  const ctx = useContext(MenuConfigContext);
  if (!ctx) {
    return { config: MENU_CONFIG_DEFAULT, salvar: () => undefined, carregando: false };
  }
  return ctx;
}
